import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {ModelsService} from "../../core/models.service";
import {SearchReferenceService} from "../../core/search-reference.service";
import {SearchService} from "../../core/search.service";
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {SearchParameters} from "../../core/definitions/search-parameters";
import {SearchObject} from "../../core/definitions/search-object";
import {FormControl, ReactiveFormsModule} from "@angular/forms";
import {MatFormField, MatLabel} from "@angular/material/form-field";
import {MatOptgroup, MatOption, MatSelect} from "@angular/material/select";

interface SuperobjectFilterGroupItem {
  value: string;
  name: string;
}

interface SuperobjectFilterGroup {
  name: string;
  groupId: string;
  children: SuperobjectFilterGroupItem[];
}

@Component({
  selector: 'app-superobject-filter-select',
  host: {'class': 'superobject-filter-select'},
  imports: [
    MatFormField,
    MatLabel,
    MatSelect,
    ReactiveFormsModule,
    MatOptgroup,
    MatOption,
    TranslateModule
  ],
  templateUrl: './superobject-filter-select.component.html',
  styleUrl: './superobject-filter-select.component.scss'
})
export class SuperobjectFilterSelectComponent implements OnInit {
  @Input() conceptTypeId: string;
  @Input() parentSuperobjectTypeId: string;
  @Output() filterChange = new EventEmitter<string[]>();

  superobjectFilterGroups: SuperobjectFilterGroup[] = [];
  superobjectFilterGroupControl: FormControl = new FormControl();

  constructor(private translateService: TranslateService,
              private modelsService: ModelsService,
              private searchReferenceService: SearchReferenceService,
              private searchService: SearchService) {
  }

  ngOnInit(): void {
    this.setReferenceFilter().then(() => {
      this.setSuperobjectFilterGroupControl();
    });
  }

  private async setReferenceFilter() {
    const model = await this.modelsService.getModelAsync(this.conceptTypeId);
    if (model.$$meta.filters) {
      const referenceId = model.$$meta.filters.reference_id;
      if (referenceId) {
        const reference = this.searchReferenceService.getSearchReferenceFromReferenceId(referenceId);
        if (reference?.ref_filter) {
          const filters = reference.ref_filter.map(refFilter => refFilter.fq);
          filters.push(`object_type:"${reference.object_type}"`);
          const searchParams = {
            fq: filters,
            fl: ['artifact_id', 'artifact_name', 'level', 'is_leaf', 'parent_id', 'parent_id_value', 'm_path'],
          } as SearchParameters;
          const res = await this.searchService.search(searchParams);
          this.superobjectFilterGroups = this.makeSuperobjectFilterGroups(res.artifacts);
        }
      }
    }
  }

  private setSuperobjectFilterGroupControl() {
    const filterValues = [];
    if (this.parentSuperobjectTypeId) {
      filterValues.push(this.parentSuperobjectTypeId);
    }
    this.superobjectFilterGroupControl = new FormControl({
      value: filterValues,
      disabled: !this.superobjectFilterGroups.length
    });
    this.superobjectFilterGroupControl.valueChanges.subscribe(value => {
      this.filterChange.emit(value);
    })
  }

  private makeSuperobjectFilterGroups(superobjectFilters: SearchObject[]): SuperobjectFilterGroup[] {
    const superobjectFilterGroups: SuperobjectFilterGroup[] = [];
    let index = 0;
    let loopCount = 0; // To prevent infinite loop
    if (!superobjectFilters.length) {
      return superobjectFilterGroups;
    }
    do {
      let added = false;
      const superobjectFilter = superobjectFilters[index];
      if (superobjectFilter.level === 1) {
        this.addSuperobjectFilterGroup(superobjectFilterGroups, superobjectFilter);
        superobjectFilters.splice(index, 1);
        added = true;
      } else {
        added = this.checkAddToSuperobjectFilterGroup(superobjectFilterGroups, superobjectFilter);
        if (added) {
          superobjectFilters.splice(index, 1);
        }
      }
      if (!added) {
        index++;
        if (index >= superobjectFilters.length) {
          index = 0;
          console.log('Loop count', loopCount);
        }
      }
      loopCount++;
    } while (superobjectFilters.length && loopCount < 1000);
    return superobjectFilterGroups;
  }

  private addSuperobjectFilterGroup(superobjectFilterGroups: SuperobjectFilterGroup[], superobjectFilter: SearchObject) {
    const allText = this.translateService.instant('TRANS__KULTUR_NAV_SEARCH__ALL');
    const newChild = {
      name: `${superobjectFilter.artifact_name} (${allText})`,
      value: superobjectFilter.artifact_id
    }
    const newGroup = {
      name: superobjectFilter.artifact_name,
      groupId: superobjectFilter.artifact_id,
      children: [newChild]
    }
    superobjectFilterGroups.push(newGroup);
    superobjectFilter.children = []
  }

  private checkAddToSuperobjectFilterGroup(superobjectFilterGroups: SuperobjectFilterGroup[], superobjectFilter: SearchObject) {
    let added = false;
    for (let group of superobjectFilterGroups) {
      if (group.groupId === superobjectFilter.parent_id) {
        const newChild = {
          name: superobjectFilter.artifact_name,
          value: superobjectFilter.artifact_id
        }
        group.children.push(newChild);
        added = true;
        break;
      }
    }
    return added;
  }

}
